import React, { useState, useEffect } from "react";
import {
  Container,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import axios from "axios";
import "../../../assets/css/admindashboard.css";
import AdminHeader from "../AdminHeader";
import AdminLeftNav from "../AdminLeftNav";
import { useAuth } from "../../context/AuthContext";
import "../../../assets/css/table.css";

// --- Constants for API ---
const BASE_URL = "https://mahadevaaya.com/spindo/spindobackend";
const API_URL = `${BASE_URL}/api/service-category/`;

const ManageServiceCategory = () => {
  const { tokens } = useAuth();

  // Check device width
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Service categories state
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Edit form state (shown on page instead of modal)
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    prod_name: "",
    prod_desc: "",
    prod_cate: "",
    sub_cate: "",
    prod_img: "",
    status: "draft",
  });

  // Form submission states
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      setSidebarOpen(width >= 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);

    // Fetch categories on component mount
    fetchCategories();

    return () => window.removeEventListener("resize", checkDevice);
  }, [tokens]);

  // Fetch categories from API
  const fetchCategories = async () => {
    if (!tokens?.access) return;

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${tokens.access}`,
          "Content-Type": "application/json",
        },
      });

      setCategories(response.data.data || []);
    } catch (err) {
      console.error("FETCH ERROR:", err.response?.data || err.message);
      setError(
        err.response?.data?.message ||
          "Failed to fetch service categories. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission (POST & PUT)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tokens?.access) {
      setError("Authentication required. Please log in.");
      return;
    }

    setSubmitLoading(true);
    setError("");
    setSuccess("");

    try {
      const formDataToSend = new FormData();

      if (editingId) {
        // PUT (UPDATE)
        formDataToSend.append("id", editingId);
        formDataToSend.append("prod_name", formData.prod_name);
        formDataToSend.append("prod_desc", formData.prod_desc);
        formDataToSend.append("prod_cate", formData.prod_cate);
        formDataToSend.append("sub_cate", formData.sub_cate);
        formDataToSend.append("status", formData.status);
        if (formData.prod_img && typeof formData.prod_img !== "string") {
          formDataToSend.append(
            "prod_img",
            formData.prod_img,
            formData.prod_img.name,
          );
        }

        await axios.put(API_URL, formDataToSend, {
          headers: {
            Authorization: `Bearer ${tokens.access}`,
            "Content-Type": "multipart/form-data",
          },
        });

        alert("Service category updated successfully!");
        setShowEditForm(false);
      } else {
        // POST (CREATE)
        formDataToSend.append("prod_name", formData.prod_name);
        formDataToSend.append("prod_desc", formData.prod_desc);
        formDataToSend.append("prod_cate", formData.prod_cate);
        formDataToSend.append("sub_cate", formData.sub_cate);
        formDataToSend.append("status", formData.status);
        if (formData.prod_img && typeof formData.prod_img !== "string") {
          formDataToSend.append(
            "prod_img",
            formData.prod_img,
            formData.prod_img.name,
          );
        }

        await axios.post(API_URL, formDataToSend, {
          headers: {
            Authorization: `Bearer ${tokens.access}`,
            "Content-Type": "multipart/form-data",
          },
        });

        setSuccess("Service category added successfully!");
        setShowAddModal(false);
      }

      resetForm();
      fetchCategories();
    } catch (err) {
      console.error("SUBMIT ERROR:", err.response?.data || err.message);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        "Something went wrong";
      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit button click
  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormData({
      prod_name: category.prod_name,
      prod_desc: category.prod_desc,
      prod_cate: category.prod_cate,
      sub_cate: category.sub_cate,
      prod_img: category.prod_img || "",
      status: category.status,
    });
    setShowEditForm(true);
    // Scroll to top of the form
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  // Handle delete button click
  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!tokens?.access) {
      setError("Authentication required. Please log in.");
      return;
    }

    setSubmitLoading(true);
    setError("");
    setSuccess("");

    try {
      const deletePayload = {
        id: selectedCategory.id,
      };
      await axios.delete(API_URL, {
        headers: {
          Authorization: `Bearer ${tokens.access}`,
          "Content-Type": "application/json",
        },
        data: deletePayload,
      });

      alert("Service category deleted successfully!");
      setShowDeleteModal(false);
      fetchCategories();
    } catch (err) {
      console.error("DELETE ERROR:", err.response?.data || err.message);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        "Failed to delete service category";
      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setEditingId(null);
    setFormData({
      prod_name: "",
      prod_desc: "",
      prod_cate: "",
      sub_cate: "",
      prod_img: null,
      status: "draft",
    });
  };

  // Handle add modal close
  const handleAddModalClose = () => {
    setShowAddModal(false);
    resetForm();
  };

  // Handle edit form close
  const handleEditFormClose = () => {
    setShowEditForm(false);
    resetForm();
  };

  return (
    <>
      <div className="dashboard-container">
        {/* Left Sidebar */}
        <AdminLeftNav
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          isMobile={isMobile}
          isTablet={isTablet}
        />

        {/* Main Content */}
        <div className="main-content-dash">
          <AdminHeader toggleSidebar={toggleSidebar} />

          <Container fluid className="dashboard-body dashboard-main-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2>Service Categories</h2>
              <Button variant="primary" onClick={() => setShowAddModal(true)}>
                Add New Category
              </Button>
            </div>

            {success && (
              <Alert
                variant="success"
                dismissible
                onClose={() => setSuccess("")}
              >
                {success}
              </Alert>
            )}

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            {/* Edit Form (shown on page when edit is clicked) */}
            {showEditForm && (
              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0">Edit Service Category</h4>
                  <Button variant="secondary" onClick={handleEditFormClose}>
                    <i className="fas fa-times"></i> Cancel
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSubmit} encType="multipart/form-data">
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="edit_prod_name">
                          <Form.Label>Product Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter Product Name"
                            name="prod_name"
                            value={formData.prod_name}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="edit_prod_cate">
                          <Form.Label>Category</Form.Label>
                          <Form.Select
                            name="prod_cate"
                            value={formData.prod_cate}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select category</option>
                            <option value="Home Services">Home Services</option>
                            <option value="Electronics">Electronics</option>
                            <option value="Beauty">Beauty</option>
                            <option value="Healthcare">Healthcare</option>
                            <option value="Education">Education</option>
                            <option value="Other">Other</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="edit_sub_cate">
                          <Form.Label>Subcategory</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter subcategory"
                            name="sub_cate"
                            value={formData.sub_cate}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="edit_status">
                          <Form.Label>Status</Form.Label>
                          <Form.Select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            required
                          >
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3" controlId="edit_prod_desc">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter description"
                        name="prod_desc"
                        value={formData.prod_desc}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="edit_prod_img">
                      <Form.Label>Product Image</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        name="prod_img"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          setFormData((prev) => ({
                            ...prev,
                            prod_img: file,
                          }));
                        }}
                      />
                      {formData.prod_img &&
                        typeof formData.prod_img !== "string" && (
                          <div className="mt-2">
                            <small className="text-muted">
                              Selected file: {formData.prod_img.name}
                            </small>
                          </div>
                        )}
                      {formData.prod_img &&
                        typeof formData.prod_img === "string" && (
                          <div className="mt-2">
                            <small className="text-muted">Current image:</small>
                            <div className="mt-1">
                              <img
                                src={`${BASE_URL}/${formData.prod_img}`}
                                alt="Current product image"
                                style={{
                                  width: "100px",
                                  height: "100px",
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                }}
                              />
                            </div>
                            <div className="mt-1">
                              <small className="text-muted">
                                URL: {formData.prod_img}
                              </small>
                            </div>
                          </div>
                        )}
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      disabled={submitLoading}
                    >
                      {submitLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                          <span className="ms-2">Updating...</span>
                        </>
                      ) : (
                        "Update Category"
                      )}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            )}

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <Row>
                {categories.length > 0 ? (
                  categories.map((category) => (
                    <Col md={4} className="mb-4" key={category.id}>
                      <Card className="h-100">
                        <Card.Img
                          variant="top"
                          src={
                            category.prod_img
                              ? `${BASE_URL}/${category.prod_img}`
                              : "https://via.placeholder.com/300x200?text=No+Image"
                          }
                          style={{ height: "200px", objectFit: "cover" }}
                        />
                        <Card.Body className="d-flex flex-column">
                          <Card.Title>{category.prod_name}</Card.Title>
                          <Card.Text className="flex-grow-1">
                            <small className="text-muted">
                              Category: {category.prod_cate}
                            </small>
                            <br />
                            <small className="text-muted">
                              Subcategory: {category.sub_cate}
                            </small>
                            <br />
                            <small className="text-muted">
                              Description:{" "}
                              {category.prod_desc
                                ? category.prod_desc.substring(0, 100) + "..."
                                : "No description"}
                            </small>
                          </Card.Text>
                          <div className="d-flex justify-content-between align-items-center">
                            <span
                              className={`badge bg-${category.status === "draft" ? "secondary" : "success"}`}
                            >
                              {category.status}
                            </span>
                            <div>
                              <Button
                                variant="info"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEdit(category)}
                              >
                                <i className="fas fa-edit"></i> Edit
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteClick(category)}
                              >
                                <i className="fas fa-trash"></i> Delete
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))
                ) : (
                  <Col>
                    <div className="text-center py-5">
                      <p>No service categories found</p>
                    </div>
                  </Col>
                )}
              </Row>
            )}
          </Container>
        </div>
      </div>

      {/* Add Category Modal */}
      <Modal show={showAddModal} onHide={handleAddModalClose} size="lg">
        <Modal.Header closeButton className="modal-top">
          <Modal.Title>Add New Service Category</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} encType="multipart/form-data">
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="prod_name">
                  <Form.Label>Product Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Product Name"
                    name="prod_name"
                    value={formData.prod_name}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="prod_cate">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    name="prod_cate"
                    value={formData.prod_cate}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select category</option>
                    <option value="Home Services">Home Services</option>
                    <option value="Electronics">Electronics</option>
                    <option value="Beauty">Beauty</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Education">Education</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="sub_cate">
                  <Form.Label>Subcategory</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter subcategory"
                    name="sub_cate"
                    value={formData.sub_cate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="status">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="prod_desc">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter description"
                name="prod_desc"
                value={formData.prod_desc}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="prod_img">
              <Form.Label>Product Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                name="prod_img"
                onChange={(e) => {
                  const file = e.target.files[0];
                  setFormData((prev) => ({
                    ...prev,
                    prod_img: file,
                  }));
                }}
              />
              {formData.prod_img && typeof formData.prod_img !== "string" && (
                <div className="mt-2">
                  <small className="text-muted">
                    Selected file: {formData.prod_img.name}
                  </small>
                </div>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleAddModalClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitLoading}>
              {submitLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">Adding...</span>
                </>
              ) : (
                "Add Category"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete "{selectedCategory?.prod_name}"? This
          action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={submitLoading}
          >
            {submitLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ms-2">Deleting...</span>
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ManageServiceCategory;
